import { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import type { UserRowV2 } from "./sql";
import { STAGES, deepestStageBeforePaid } from "./helpers";
import type { StageKey } from "./helpers";
import { daysBetween } from "../../lib/funnel/helpers";
import { PRIMARY, MUTED, FG1, VIOLET, ORANGE, RED, STEEL, GRAY } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import { fmtN } from "../../lib/funnel/helpers";
import type { FilterPillSpec } from "../../components/FilterPills";

const BUCKETS: { label: string; min: number; max: number }[] = [
  { label: "0–7d",    min: 0,   max: 7 },
  { label: "8–30d",   min: 8,   max: 30 },
  { label: "31–60d",  min: 31,  max: 60 },
  { label: "61–90d",  min: 61,  max: 90 },
  { label: "91–180d", min: 91,  max: 180 },
  { label: "181d+",   min: 181, max: Infinity },
];

const SEGMENT_ORDER: { key: StageKey | "none"; label: string; color: string }[] = [
  { key: "none",             label: "No prior milestone",     color: GRAY },
  { key: "calendar",         label: "Connected calendar",     color: STEEL },
  { key: "first_bot",        label: "First bot meeting",      color: VIOLET },
  { key: "first_transcript", label: "First transcript",       color: ORANGE },
  { key: "first_synopsis",   label: "First summary",          color: PRIMARY },
  { key: "second_synopsis",  label: "Second summary",         color: RED },
  { key: "first_chat",       label: "Asked Marloo",           color: "#8C5BB8" },
];

type ChartRow = Record<string, number | string> & { label: string };

export function TimeToPaidPanel({
  users,
  pagePills,
}: {
  users: UserRowV2[];
  pagePills?: FilterPillSpec[];
}) {
  const { rows, totalPaid } = useMemo(() => {
    const paidUsers = users.filter((u) => u.converted_to_paid_at);
    const out: ChartRow[] = BUCKETS.map((b) => {
      const row: ChartRow = { label: b.label };
      SEGMENT_ORDER.forEach((s) => { row[s.key] = 0; });
      return row;
    });
    for (const u of paidUsers) {
      const days = daysBetween(u.created_at_utc, u.converted_to_paid_at!);
      if (!Number.isFinite(days) || days < 0) continue;
      const bIdx = BUCKETS.findIndex((b) => days >= b.min && days <= b.max);
      if (bIdx < 0) continue;
      const deepest = deepestStageBeforePaid(u);
      const row = out[bIdx];
      if (!row) continue;
      row[deepest] = (Number(row[deepest]) || 0) + 1;
    }
    return { rows: out, totalPaid: paidUsers.length };
  }, [users]);

  return (
    <section className="card">
      <SectionCardHeader
        title="Time-to-paid by deepest milestone reached first"
        subtitle="Of users who converted to paid, the bar shows when they paid (days since signup), stacked by the deepest non-paid milestone they had hit before paying. Reads as: which activation depth tends to precede conversion, and how fast?"
        filterPills={[
          ...(pagePills ?? []),
          { kind: "mode", label: `${fmtN(totalPaid)} paid users in cohort` },
        ]}
      />
      <div className="card-body">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: FG1 }} />
            <YAxis tick={{ fontSize: 10, fill: MUTED }} allowDecimals={false} width={36} />
            <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {SEGMENT_ORDER.map((s, i) => {
              const isTop = i === SEGMENT_ORDER.length - 1;
              const stageLabel = s.key === "none"
                ? "No prior milestone"
                : (STAGES.find((st) => st.key === s.key)?.label ?? s.label);
              return (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={stageLabel}
                  stackId="paid"
                  fill={s.color}
                  radius={isTop ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                  isAnimationActive={false}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
